import React, { useState, useRef } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  AppBar,
  Toolbar,
  Avatar,
  Divider,
  Chip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Alert,
  CircularProgress,
  Stack,
  LinearProgress,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import HomeIcon from '@mui/icons-material/Home';
import AddLocationIcon from '@mui/icons-material/AddLocation';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import VideocamIcon from '@mui/icons-material/Videocam';
import CloseIcon from '@mui/icons-material/Close';
import PhoneIcon from '@mui/icons-material/Phone';
import LanguageIcon from '@mui/icons-material/Language';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useNavigate, useLocation } from 'react-router-dom';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://mkcx4gafu6gbomo3buhjkn7lpa0pxqqw.lambda-url.us-east-1.on.aws';
const getAuthToken = () => localStorage.getItem('token') || '';

interface SubmittedState {
  stationId: string;
  locationId: string;
  address: string;
  name: string;
  lat: number;
  lng: number;
  locationType: string;
  primaryImageUrl?: string | null;
  isAccessible?: boolean;
  isGenderNeutral?: boolean;
  notes?: string;
}

/**
 * SpotLocationDisclosure — Step 2: Enrichment page shown after station creation.
 *
 * Lets the user optionally add:
 *   ✅ Gallery photos (up to 10)
 *   ✅ Video
 *   ✅ Hours of operation
 *   ✅ Table type & weight limit
 *   ✅ Phone & website
 */
const SpotLocationDisclosure: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as SubmittedState | null;

  // Fallbacks
  const stationId = state?.stationId || '';
  const locationId = state?.locationId || '';
  const address = state?.address || 'Your submitted location';
  const name = state?.name || address;
  const primaryImageUrl = state?.primaryImageUrl || null;

  // ── Gallery state ──────────────────────────────────────────────────
  const [galleryFiles, setGalleryFiles] = useState<File[]>([]);
  const [galleryPreviews, setGalleryPreviews] = useState<string[]>([]);
  const [galleryUrls, setGalleryUrls] = useState<string[]>([]);
  const [galleryUploading, setGalleryUploading] = useState(false);
  const [galleryDone, setGalleryDone] = useState(false);
  const galleryRef = useRef<HTMLInputElement>(null);

  // ── Video state ────────────────────────────────────────────────────
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [videoPreview, setVideoPreview] = useState<string | null>(null);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [videoUploading, setVideoUploading] = useState(false);
  const [videoDone, setVideoDone] = useState(false);
  const videoRef = useRef<HTMLInputElement>(null);

  // ── Detail fields ──────────────────────────────────────────────────
  const [hoursOfOperation, setHoursOfOperation] = useState('');
  const [tableType, setTableType] = useState('');
  const [weightLimit, setWeightLimit] = useState<number | ''>('');
  const [phone, setPhone] = useState('');
    const [website, setWebsite] = useState('');
    const [detailsSaved, setDetailsSaved] = useState(false);
    const [detailsSaving, setDetailsSaving] = useState(false);

    // ── General ────────────────────────────────────────────────────────
    const [error, setError] = useState<string | null>(null);
    const [showDetails, setShowDetails] = useState(false);

    // ═════════════════════════════════════════════════════════════════
    //  Gallery upload
    // ═════════════════════════════════════════════════════════════════
    const handleGallerySelect = (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (!files) return;
      const newFiles: File[] = [];
      const newPreviews: string[] = [];
      for (let i = 0; i < files.length && galleryFiles.length + newFiles.length < 10; i++) {
        const f = files[i];
        if (!f.type.startsWith('image/') || f.size > 10 * 1024 * 1024) continue;
        newFiles.push(f);
        newPreviews.push(URL.createObjectURL(f));
      }
      setGalleryFiles((prev) => [...prev, ...newFiles]);
      setGalleryPreviews((prev) => [...prev, ...newPreviews]);
    };

    const removeGalleryItem = (idx: number) => {
      setGalleryFiles((prev) => prev.filter((_, i) => i !== idx));
      setGalleryPreviews((prev) => {
        const url = prev[idx];
        if (url) URL.revokeObjectURL(url);
        return prev.filter((_, i) => i !== idx);
      });
    };

    const uploadGallery = async () => {
      if (galleryFiles.length === 0 || !stationId) return;
      setGalleryUploading(true);
      setError(null);
      const urls: string[] = [];
      const token = getAuthToken();

      for (const file of galleryFiles) {
        try {
          // 1) Get presigned upload URL (GET /photos/upload-url)
          const params = new URLSearchParams({
            stationId,
            contentType: file.type,
            category: 'changing_station',
          });
          const urlRes = await fetch(`${API_BASE_URL}/photos/upload-url?${params.toString()}`, {
            method: 'GET',
            headers: { Authorization: `Bearer ${token}` },
          });
          if (!urlRes.ok) throw new Error('Gallery upload URL failed');
          const { uploadUrl, photoId, s3Key } = await urlRes.json();

          // 2) PUT to S3
          const putRes = await fetch(uploadUrl, { method: 'PUT', headers: { 'Content-Type': file.type }, body: file });
          if (!putRes.ok) throw new Error('S3 upload failed');

          // 3) Confirm upload (POST /photos/confirm)
          const confRes = await fetch(`${API_BASE_URL}/photos/confirm`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({
              photoId,
              s3Key,
              stationId,
              category: 'changing_station',
            }),
          });
          if (!confRes.ok) throw new Error('Gallery confirm failed');
          const cd = await confRes.json();
          if (cd && cd.imageUrl) urls.push(cd.imageUrl);
        } catch (err) {
          console.warn('[Gallery] Non-blocking upload error:', err);
        }
      }
      // Update station gallery only once after all uploads
      if (urls.length > 0) {
        debugger
        try {
          await fetch(`${API_BASE_URL}/stations/${stationId}`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              gallery: urls
            }),
          });
        } catch (err) {
          debugger
          console.warn('[Gallery] Failed to update gallery:', err);
        }
      }
      setGalleryUrls(urls);
      setGalleryUploading(false);
      setGalleryDone(true);
    };

    // ═════════════════════════════════════════════════════════════════
    //  Video upload
    // ═════════════════════════════════════════════════════════════════
    const handleVideoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      if (!file.type.startsWith('video/') || file.size > 100 * 1024 * 1024) {
        setError('Video must be under 100 MB');
        return;
      }
      setVideoFile(file);
      setVideoPreview(URL.createObjectURL(file));
    };

    const removeVideo = () => {
      setVideoFile(null);
      if (videoPreview) URL.revokeObjectURL(videoPreview);
      setVideoPreview(null);
    };

    const uploadVideo = async () => {
      if (!videoFile || !stationId) return;
      setVideoUploading(true);
      setError(null);
      const token = getAuthToken();

      try {
        const urlRes = await fetch(`${API_BASE_URL}/stations/${stationId}/video/upload-url`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ contentType: videoFile.type }),
        });
        if (!urlRes.ok) throw new Error('Video upload URL failed');
        const { uploadUrl, s3Key } = await urlRes.json();

        const putRes = await fetch(uploadUrl, { method: 'PUT', headers: { 'Content-Type': videoFile.type }, body: videoFile });
        if (!putRes.ok) throw new Error('S3 upload failed');

        const confRes = await fetch(`${API_BASE_URL}/stations/${stationId}/video/confirm`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ s3Key }),
        });
        if (!confRes.ok) throw new Error('Video confirm failed');
        const cd = await confRes.json();
        const confirmedVideoUrl = cd.videoUrl || null;
        setVideoUrl(confirmedVideoUrl);
        setVideoDone(true);

        // Update station with videoUrl
        if (confirmedVideoUrl) {
          await fetch(`${API_BASE_URL}/stations/${stationId}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({ videoUrl: confirmedVideoUrl }),
          });
        }
      } catch (err: any) {
        setError(err.message || 'Video upload failed');
      } finally {
        setVideoUploading(false);
      }
    };

    // ═════════════════════════════════════════════════════════════════
    //  Save additional details (PATCH station + location)
    // ═════════════════════════════════════════════════════════════════
    const saveDetails = async () => {
      setDetailsSaving(true);
      setError(null);
      const token = getAuthToken();

      try {
        // Update station fields
        const stationBody: Record<string, any> = {};
        if (tableType) stationBody.tableType = tableType;
        if (weightLimit) stationBody.weightLimit = Number(weightLimit);

        if (Object.keys(stationBody).length > 0) {
          await fetch(`${API_BASE_URL}/stations/${stationId}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify(stationBody),
          });
        }

        // Update location fields
        const locBody: Record<string, any> = {};
        if (hoursOfOperation.trim()) locBody.hoursOfOperation = hoursOfOperation.trim();
        if (phone.trim()) locBody.phoneNumber = phone.trim();
        if (website.trim()) locBody.websiteUrl = website.trim();

        if (Object.keys(locBody).length > 0 && locationId) {
          await fetch(`${API_BASE_URL}/locations/${locationId}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify(locBody),
          });
        }

        setDetailsSaved(true);
      } catch (err: any) {
        setError(err.message || 'Failed to save details');
      } finally {
        setDetailsSaving(false);
      }
    };

    // ═════════════════════════════════════════════════════════════════
    //  Render
    // ═════════════════════════════════════════════════════════════════
    return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#FAFAFA' }}>
      {/* App Bar */}
      <AppBar position="static" elevation={0} sx={{ bgcolor: '#E94057' }}>
        <Toolbar>
          <Avatar sx={{ bgcolor: 'transparent', width: 32, height: 32, mr: 1 }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C8.5 2 5.5 4.5 5.5 8c0 1.5.5 2.9 1.4 4l-2.9 7h3l1-2.5h6l1 2.5h3l-2.9-7c.9-1.1 1.4-2.5 1.4-4 0-3.5-3-6-6.5-6zm-3 12.5l1-2.5h4l1 2.5H9zm3-5.5c-1.7 0-3-1.3-3-3s1.3-3 3-3 3 1.3 3 3-1.3 3-3 3z"/>
            </svg>
          </Avatar>
          <Typography
            variant="h6"
            data-testid="app-title"
            sx={{ flexGrow: 1, fontWeight: 700, color: '#fff', fontFamily: '"Poppins", sans-serif' }}
          >
            BabyOnTheGo
          </Typography>
        </Toolbar>
      </AppBar>

      <Container maxWidth="sm" sx={{ py: 4 }}>
        {/* ─── Success banner ──────────────────────────────────── */}
        <Box display="flex" flexDirection="column" alignItems="center" sx={{ mb: 3 }}>
          <Box
            sx={{
              width: 80, height: 80, borderRadius: '50%', bgcolor: '#E8F5E9',
              display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1.5,
            }}
            data-testid="success-icon"
          >
            <CheckCircleIcon sx={{ fontSize: 48, color: '#4CAF50' }} />
          </Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#333', mb: 0.5, textAlign: 'center' }} data-testid="success-title">
            Station Created! 🎉
          </Typography>
          <Typography variant="body2" sx={{ color: '#888', textAlign: 'center' }}>
            Now let's make it shine — add photos, video &amp; details so other parents know what to expect.
          </Typography>
        </Box>

        {/* ─── Location summary chip ───────────────────────────── */}
        <Paper elevation={0} sx={{ p: 2, borderRadius: 3, mb: 3, border: '1px solid #E0E0E0' }} data-testid="location-details-card">
          <Box display="flex" alignItems="center" gap={1}>
            <LocationOnIcon sx={{ color: '#E94057' }} />
            <Box>
              <Typography variant="body1" sx={{ fontWeight: 600, color: '#333' }}>{name}</Typography>
              <Typography variant="caption" sx={{ color: '#888' }} data-testid="submitted-address">{address}</Typography>
            </Box>
          </Box>
          {primaryImageUrl && (
            <Box sx={{ mt: 1.5, borderRadius: 2, overflow: 'hidden', maxHeight: 140 }}>
              <img src={primaryImageUrl} alt="Primary" style={{ width: '100%', objectFit: 'cover' }} />
            </Box>
          )}
        </Paper>

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

        {/* ═══════════ Gallery ═══════════ */}
        <Paper elevation={0} sx={{ p: 3, borderRadius: 3, mb: 2.5, border: '1px solid #E0E0E0' }}>
          <Typography variant="subtitle1" sx={{ mb: 0.5, fontWeight: 600 }}>
            🖼️ Gallery Photos
            <Typography component="span" variant="caption" sx={{ ml: 1, color: '#aaa' }}>up to 10</Typography>
          </Typography>
          <Typography variant="caption" sx={{ display: 'block', mb: 2, color: '#888' }}>
            Multiple angles help parents feel confident about the spot.
          </Typography>

          {/* Thumbnails */}
          {galleryPreviews.length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
              {galleryPreviews.map((src, idx) => (
                <Box key={idx} sx={{ position: 'relative', width: 'calc(25% - 6px)', pt: 'calc(25% - 6px)', borderRadius: 2, overflow: 'hidden', border: '1px solid #eee' }}>
                  <img src={src} alt={`gallery-${idx}`} style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
                  <IconButton
                    size="small"
                    onClick={() => removeGalleryItem(idx)}
                    sx={{ position: 'absolute', top: 2, right: 2, bgcolor: 'rgba(0,0,0,.5)', color: '#fff', p: 0.3, '&:hover': { bgcolor: 'rgba(0,0,0,.7)' } }}
                  >
                    <CloseIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </Box>
              ))}
            </Box>
          )}

          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              component="label"
              startIcon={<PhotoCameraIcon />}
              disabled={galleryUploading || galleryDone || galleryFiles.length >= 10}
              sx={{ borderColor: '#E94057', color: '#E94057', borderRadius: 2, textTransform: 'none', '&:hover': { borderColor: '#d63649' } }}
            >
              Add Photos
              <input type="file" accept="image/*" multiple hidden ref={galleryRef} onChange={handleGallerySelect} />
            </Button>
            {galleryFiles.length > 0 && !galleryDone && (
              <Button
                variant="contained"
                onClick={uploadGallery}
                disabled={galleryUploading}
                sx={{ bgcolor: '#E94057', borderRadius: 2, textTransform: 'none', '&:hover': { bgcolor: '#d63649' } }}
              >
                {galleryUploading ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : `Upload ${galleryFiles.length}`}
              </Button>
            )}
          </Stack>
          {galleryDone && (
            <Alert severity="success" sx={{ mt: 1.5 }}>
              ✅ {galleryUrls.length} gallery photo{galleryUrls.length !== 1 ? 's' : ''} uploaded!
            </Alert>
          )}
        </Paper>

        {/* ═══════════ Video ═══════════ */}
        <Paper elevation={0} sx={{ p: 3, borderRadius: 3, mb: 2.5, border: '1px solid #E0E0E0' }}>
          <Typography variant="subtitle1" sx={{ mb: 0.5, fontWeight: 600 }}>
            🎬 Video Walkthrough
            <Typography component="span" variant="caption" sx={{ ml: 1, color: '#aaa' }}>optional, max 100 MB</Typography>
          </Typography>
          <Typography variant="caption" sx={{ display: 'block', mb: 2, color: '#888' }}>
            A short clip showing the station and surroundings.
          </Typography>

          {videoPreview && (
            <Box sx={{ position: 'relative', mb: 2, borderRadius: 2, overflow: 'hidden', border: '1px solid #eee' }}>
              <video src={videoPreview} controls style={{ width: '100%', maxHeight: 200, objectFit: 'cover' }} />
              <IconButton
                size="small"
                onClick={removeVideo}
                sx={{ position: 'absolute', top: 6, right: 6, bgcolor: 'rgba(0,0,0,.55)', color: '#fff', '&:hover': { bgcolor: 'rgba(0,0,0,.75)' } }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          )}

          <Stack direction="row" spacing={1}>
            {!videoPreview && (
              <Button
                variant="outlined"
                component="label"
                startIcon={<VideocamIcon />}
                disabled={videoUploading || videoDone}
                sx={{ borderColor: '#E94057', color: '#E94057', borderRadius: 2, textTransform: 'none', '&:hover': { borderColor: '#d63649' } }}
              >
                Choose Video
                <input type="file" accept="video/*" hidden ref={videoRef} onChange={handleVideoSelect} />
              </Button>
            )}
            {videoFile && !videoDone && (
              <Button
                variant="contained"
                onClick={uploadVideo}
                disabled={videoUploading}
                sx={{ bgcolor: '#E94057', borderRadius: 2, textTransform: 'none', '&:hover': { bgcolor: '#d63649' } }}
              >
                {videoUploading ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Upload Video'}
              </Button>
            )}
          </Stack>
          {videoDone && (
            <Alert severity="success" sx={{ mt: 1.5 }}>✅ Video uploaded!</Alert>
          )}
        </Paper>

        {/* ═══════════ Additional Details (collapsible) ═══════════ */}
        <Paper elevation={0} sx={{ p: 3, borderRadius: 3, mb: 2.5, border: '1px solid #E0E0E0' }}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            sx={{ cursor: 'pointer' }}
            onClick={() => setShowDetails(!showDetails)}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              ⚙️ Additional Details
              <Typography component="span" variant="caption" sx={{ ml: 1, color: '#aaa' }}>optional</Typography>
            </Typography>
            {showDetails ? <ExpandLessIcon sx={{ color: '#888' }} /> : <ExpandMoreIcon sx={{ color: '#888' }} />}
          </Box>

          {showDetails && (
            <Box sx={{ mt: 2 }}>
              {/* Hours */}
              <TextField
                fullWidth
                size="small"
                label="Hours of Operation"
                placeholder="e.g., 9 AM – 9 PM or 24/7"
                value={hoursOfOperation}
                onChange={(e) => setHoursOfOperation(e.target.value)}
                InputProps={{ startAdornment: <AccessTimeIcon sx={{ mr: 1, color: '#aaa', fontSize: 20 }} /> }}
                sx={{ mb: 2 }}
              />

              {/* Table type */}
              <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                <InputLabel id="table-type-label">Table Type</InputLabel>
                <Select
                  labelId="table-type-label"
                  value={tableType}
                  label="Table Type"
                  onChange={(e) => setTableType(e.target.value as string)}
                >
                  <MenuItem value="">Select type</MenuItem>
                  <MenuItem value="wall-mounted">Wall-mounted</MenuItem>
                  <MenuItem value="fold-down">Fold-down</MenuItem>
                  <MenuItem value="countertop">Countertop</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </Select>
              </FormControl>
              {/* Weight limit */}
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Weight Limit (lbs)"
                value={weightLimit}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setWeightLimit(e.target.value ? Number(e.target.value) : '')}
                inputProps={{ min: 0, max: 100 }}
                sx={{ mb: 2 }}
              />

              {/* Phone */}
              <TextField
                fullWidth
                size="small"
                label="Phone Number"
                placeholder="(555) 123-4567"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                InputProps={{ startAdornment: <PhoneIcon sx={{ mr: 1, color: '#aaa', fontSize: 20 }} /> }}
                sx={{ mb: 2 }}
              />

              {/* Website */}
              <TextField
                fullWidth
                size="small"
                label="Website"
                placeholder="https://…"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
                InputProps={{ startAdornment: <LanguageIcon sx={{ mr: 1, color: '#aaa', fontSize: 20 }} /> }}
                sx={{ mb: 2 }}
              />

              <Button
                variant="contained"
                onClick={saveDetails}
                disabled={detailsSaving || detailsSaved}
                sx={{ bgcolor: '#E94057', borderRadius: 2, textTransform: 'none', '&:hover': { bgcolor: '#d63649' } }}
              >
                {detailsSaving ? (
                  <CircularProgress size={18} sx={{ color: '#fff' }} />
                ) : detailsSaved ? (
                  '✅ Saved!'
                ) : (
                  'Save Details'
                )}
              </Button>
            </Box>
          )}
        </Paper>

        <Divider sx={{ my: 3 }} />

        {/* ─── Action buttons ──────────────────────────────────── */}
        <Stack spacing={1.5}>
          <Button
            variant="contained"
            fullWidth
            onClick={() => navigate('/home')}
            startIcon={<HomeIcon />}
            data-testid="go-home-button"
            sx={{
              bgcolor: '#E94057', borderRadius: 3, textTransform: 'none',
              fontWeight: 600, py: 1.5, fontSize: '1rem',
              '&:hover': { bgcolor: '#d63649' },
            }}
          >
            Done — Back to Home
          </Button>
          <Button
            variant="outlined"
            fullWidth
            onClick={() => navigate('/post-location')}
            startIcon={<AddLocationIcon />}
            data-testid="add-another-button"
            sx={{
              borderColor: '#E94057', color: '#E94057', borderRadius: 3,
              textTransform: 'none', fontWeight: 600, py: 1.5, fontSize: '1rem',
              '&:hover': { borderColor: '#d63649', bgcolor: 'rgba(233,64,87,0.04)' },
            }}
          >
            Add Another Location
          </Button>
        </Stack>

        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Typography variant="body2" sx={{ color: '#999' }}>
            🍼 Thank you for making parenting easier for everyone!
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default SpotLocationDisclosure;
